import {memo, useMemo} from 'react';

import {useSelector} from 'react-redux';
import styled, {css} from 'styled-components';

import {
  selectCurrentRestaurant,
  selectIsUserLoggedIn,
  selectUserLoading,
} from '~/shared/store/selectors';
import {InnerWidthContainer as _InnerWidthContainer} from '~/shared/components/styled';
import {media} from '~/shared/theme/utils';
import {useIsMaxDesktop, useIsMaxLargeMobile, useIsMinLargeMobile, useIsMinLargeTablet} from '~/shared/hooks/deviceInfo';
import {flexCenterVertically, flexSpace} from '~/shared/theme/FlexLayout';
import SmallScreenMenuPageHeader from '~/shared/components/header/PageHeader/SmallScreenMenuPageHeader';
import DefaultHeader from '~/common/components/PageHeader/PageHeaderDefault';
import {HeaderAllowedFeatures} from '~/shared/components/header/types';
import useIsPageScrolled from '~/shared/components/header/useIsPageScrolled';
import {SkeletonLoader} from '~/shared/components/Loaders';

import useHideHeader from './useHideHeader';
import HeaderSkeleton from './HeaderSkeleton';

const Container = styled.header<{withBorder?: boolean; withShadow?: boolean}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  ${({theme, withShadow}) =>
    (withShadow
      ? css`
          box-shadow: ${theme.shadows.shadow4};
        `
      : css`
          box-shadow: none;
        `)};
  z-index: ${({theme}) => theme.zIndex.header};
  display: flex;
  flex-direction: column;
  ${({withBorder}) => withBorder && 'border-bottom: 1px solid rgba(0, 0, 0, 0.08);'}
  ${({theme}) => theme.hideHeader && 'display: none'};
  transition: box-shadow 0.3s ease-out;
  ${media.minDesktop`
    box-shadow: ${({theme, withShadow}) => (withShadow ? theme.shadows.shadow4 : 'none')};
  `}
`;

const HeaderBody = styled.div<{
  isScrolled?: boolean;
  withBorder?: boolean;
  withScrollAnimation?: boolean;
}>`
  height: ${({theme}) => theme.newHeader.height.mobile}px;
  ${media.minLargeMobile`
    height: ${({theme}) => theme.newHeader.height.rest}px;
  `}
  ${flexSpace}
  align-items: stretch;
  color: ${({theme}) => theme.colors.charcoal80};
  background-color: ${({theme}) => theme.colors.surface};
  transition: box-shadow 0.3s ease-out;
  ${({withBorder}) => withBorder && 'border-bottom: 1px solid rgba(0, 0, 0, 0.08);'}
  ${({theme, isScrolled, withBorder, withScrollAnimation}) =>
    (withBorder && withScrollAnimation && media.minLargeMobile`
    ${isScrolled ?
        `
      box-shadow: none;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    ` : `
      box-shadow: ${theme.shadows.shadow4};
      border-bottom: none;
    `}
  `)}
`;

const ShowOnMobileAndTabletOnlyFlex = styled.div`
  ${flexCenterVertically}
  flex: 1;
  width: 100%;
`;

const HideOnMobileAndTabletOnlyFlex = styled.div`
  display: flex;
  flex: 1;
`;

export const InnerWidthContainer = styled(_InnerWidthContainer)<{withNoMaxWidth?: boolean}>`
  padding-right: ${({theme}) => (theme.MobileHeaderSidesPadding)}px;
  padding-left: ${({theme}) => (theme.MobileHeaderSidesPadding)}px;
  ${({withNoMaxWidth}) => withNoMaxWidth && `
    max-width: 100%;
  `}
  ${({withNoMaxWidth, theme}) => media.minLargeMobile`
    ${withNoMaxWidth ? `
      padding-left: ${theme.largeMobileHeaderSidesPadding}px;
      padding-right: ${theme.largeMobileHeaderSidesPadding}px;
    ` : `
      padding-left: 0px;
      padding-right: 0px;
    `}
  `}
  ${media.minMobile`
    align-items: center;
  `}
  overflow: visible;
`;

type PageHeaderProps = HeaderAllowedFeatures & {
  onBackClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  children?: JSX.Element | JSX.Element[] | React.ReactNode;
  isMenuPage?: boolean;
  isHardLogoClick?: boolean;
  readOnlyAddress?: boolean;
  withHeaderBorder?: boolean;
  withContainerBorder?: boolean;
  withContainerShadow?: boolean;
  withScrollAnimation?: boolean;
  withNoInnerMaxWidth?: boolean;
};

const PageHeader = ({
  children,
  onBackClick,
  isMenuPage,
  isHardLogoClick,
  readOnlyAddress,
  showReorderDropdown,
  tenbis4BusinessProps,
  showAddressDropdown,
  withHeaderBorder = true,
  withContainerShadow,
  withContainerBorder,
  withScrollAnimation,
  withNoInnerMaxWidth,
}: PageHeaderProps) => {
  const currentRestaurant = useSelector(selectCurrentRestaurant);
  const isScrolled = useIsPageScrolled();
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isUserLoading = useSelector(selectUserLoading);

  const isMinLargeMobile = useIsMinLargeMobile();
  const isMaxDesktop = useIsMaxDesktop();
  const isMinLargeTablet = useIsMinLargeTablet();
  const isMaxLargeMobile = useIsMaxLargeMobile();

  const isHeaderHidden = useHideHeader({
    isMenuPage: !!isMenuPage,
  });
  
  const shouldShowContainerShadow = useMemo(() => {
    if (!withScrollAnimation) {
      return withContainerShadow;
    }

    return isMinLargeMobile ? !isScrolled : withContainerShadow;
  }, [
    isScrolled,
    isMinLargeMobile,
    withContainerShadow,
    withScrollAnimation,
  ]);

  return (
    <Container
      withShadow={shouldShowContainerShadow}
      withBorder={withContainerBorder}
    >
      {!isHeaderHidden && (
        <SkeletonLoader
          shouldShowLoader={isUserLoading}
          LoaderComponent={HeaderSkeleton}
          isMinLargeMobile={isMinLargeMobile}
          isMinLargeTablet={isMinLargeTablet}
          isMaxLargeMobile={isMaxLargeMobile}
        >
          <HeaderBody
            isScrolled={isScrolled}
            withBorder={withHeaderBorder}
            withScrollAnimation={withScrollAnimation}
          >
            <InnerWidthContainer withNoMaxWidth={withNoInnerMaxWidth}>
              {isMenuPage && currentRestaurant ? (
                <>
                  {isMaxDesktop ? (
                    <ShowOnMobileAndTabletOnlyFlex>
                      <SmallScreenMenuPageHeader onBackClick={onBackClick} />
                    </ShowOnMobileAndTabletOnlyFlex>
                  ) : (
                    <HideOnMobileAndTabletOnlyFlex>
                      <DefaultHeader
                        isHardLogoClick={isHardLogoClick}
                        readOnlyAddress={readOnlyAddress}
                        scrollTopOnOpenAddressDropdown={isMenuPage}
                        showAddressDropdown={showAddressDropdown}
                        isUserLoggedIn={isUserLoggedIn}
                      />
                    </HideOnMobileAndTabletOnlyFlex>
                  )}
                </>
              ) : (
                <DefaultHeader
                  isHardLogoClick={isHardLogoClick}
                  readOnlyAddress={readOnlyAddress}
                  scrollTopOnOpenAddressDropdown={isMenuPage}
                  showReorderDropdown={showReorderDropdown}
                  tenbis4BusinessProps={tenbis4BusinessProps}
                  showAddressDropdown={showAddressDropdown}
                  isUserLoggedIn={isUserLoggedIn}
                />
              )}
            </InnerWidthContainer>
          </HeaderBody>
        </SkeletonLoader>
      )}
      {children}
    </Container>
  );
};

export default memo(PageHeader);
